import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Users, Loader2, ArrowRight, Search } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import LeadTable from "@/components/LeadTable";
import { toast } from "sonner";

export default function LookalikeSearch() {
  const [searchParams] = useSearchParams();
  const preselectedLeadId = searchParams.get("lead_id") || "";

  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedLead, setSelectedLead] = useState(preselectedLeadId || "none");
  const [count, setCount] = useState("10");
  const [campaignId, setCampaignId] = useState("none");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/leads", { params: { status: "qualified" } }),
      api.get("/leads", { params: { status: "replied" } }),
      api.get("/campaigns"),
    ]).then(([q, r, c]) => {
      // Merge qualified + replied, deduplicate
      const merged = [...q.data, ...r.data];
      const seen = new Set();
      const deduped = merged.filter((l) => { if (seen.has(l.id)) return false; seen.add(l.id); return true; });
      setLeads(deduped);
      setCampaigns(c.data);
      if (preselectedLeadId && deduped.find((l) => l.id === preselectedLeadId)) {
        setSelectedLead(preselectedLeadId);
      }
    }).catch(() => {}).finally(() => setLoadingLeads(false));
  }, [preselectedLeadId]);

  const findLookalikes = async () => {
    if (selectedLead === "none") { toast.error("Select a lead to base the search on"); return; }
    setLoading(true);
    setResults([]);
    try {
      const r = await api.post(`/leads/${selectedLead}/lookalikes`, {
        lead_id: selectedLead,
        count: parseInt(count),
        campaign_id: campaignId !== "none" ? campaignId : undefined,
      });
      setResults(r.data);
      toast.success(`Found ${r.data.length} similar prospects`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookalike search failed");
    } finally { setLoading(false); }
  };

  const sourceLead = leads.find((l) => l.id === selectedLead);

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="label-mono mb-2">— Lead Generation</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Lookalike Search</h1>
        <p className="text-[#5C5C66] mt-2">Pick a qualified lead — AI finds 10 similar companies that match the same profile.</p>
      </div>

      <div className="bg-white border border-[#EAEAE3] rounded-2xl p-6 shadow-soft mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="md:col-span-1">
            <Label className="label-mono">Base lead (qualified/replied)</Label>
            {loadingLeads ? (
              <div className="mt-2 h-10 bg-[#F4F4EE] rounded-lg animate-pulse" />
            ) : (
              <Select value={selectedLead} onValueChange={setSelectedLead}>
                <SelectTrigger className="mt-2 rounded-lg"><SelectValue placeholder="Select a lead" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select a lead…</SelectItem>
                  {leads.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name} · {l.company || "Unknown co."}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label className="label-mono">Number of results</Label>
            <Select value={count} onValueChange={setCount}>
              <SelectTrigger className="mt-2 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["5", "10", "15", "20"].map((n) => <SelectItem key={n} value={n}>{n} leads</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-mono">Save to campaign</Label>
            <Select value={campaignId} onValueChange={setCampaignId}>
              <SelectTrigger className="mt-2 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Don't save to campaign</SelectItem>
                {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Preview of selected lead */}
        {sourceLead && (
          <div className="bg-[#FAFAF7] border border-[#EAEAE3] rounded-xl p-4 mb-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#002FA7] to-[#1E4FD6] text-white flex items-center justify-center text-sm font-bold shrink-0">
              {sourceLead.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{sourceLead.name}</div>
              <div className="text-xs text-[#5C5C66] truncate">{sourceLead.title}{sourceLead.company ? ` · ${sourceLead.company}` : ""}</div>
              <div className="text-xs text-[#9A9AA3] truncate">{sourceLead.industry}{sourceLead.location ? ` · ${sourceLead.location}` : ""}</div>
            </div>
            <div className="font-mono font-black text-2xl text-[#002FA7]">{sourceLead.score}</div>
          </div>
        )}

        {!loadingLeads && leads.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-sm text-amber-800">
            No qualified or replied leads yet. Mark some leads as <strong>qualified</strong> or <strong>replied</strong> to use lookalike search.{" "}
            <Link to="/app/leads" className="underline font-medium">View leads →</Link>
          </div>
        )}

        <Button onClick={findLookalikes} disabled={loading || selectedLead === "none"} className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white" data-testid="lookalike-btn">
          {loading
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Finding similar companies…</>
            : <><Search className="h-4 w-4 mr-2" /> Find lookalikes <ArrowRight className="h-4 w-4 ml-2" /></>}
        </Button>
      </div>

      {loading && (
        <div className="bg-white border border-[#EAEAE3] rounded-2xl p-12 text-center shadow-soft">
          <Loader2 className="h-8 w-8 animate-spin text-[#002FA7] mx-auto mb-4" />
          <div className="font-display text-lg font-bold tracking-tighter">Searching for similar profiles…</div>
          <p className="text-[#5C5C66] text-sm mt-2">AI is finding companies that match your best lead's profile.</p>
        </div>
      )}

      {results.length > 0 && !loading && (
        <div className="bg-white border border-[#EAEAE3] rounded-2xl shadow-soft overflow-hidden">
          <div className="p-5 border-b border-[#EAEAE3]">
            <div className="font-display text-lg font-bold tracking-tighter">{results.length} lookalike prospects found</div>
            <div className="text-xs text-[#5C5C66] mt-0.5">Based on {sourceLead?.name} · {sourceLead?.company}</div>
          </div>
          <LeadTable leads={results} onRefresh={() => {}} />
        </div>
      )}

      {!loading && results.length === 0 && selectedLead === "none" && (
        <div className="bg-[#FAFAF7] border border-dashed border-[#EAEAE3] rounded-2xl p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-white border border-[#EAEAE3] flex items-center justify-center mx-auto mb-4 shadow-soft">
            <Users className="h-7 w-7 text-[#9A9AA3]" />
          </div>
          <div className="font-display text-xl font-bold tracking-tighter mb-2">Find your next best lead</div>
          <p className="text-[#5C5C66] text-sm">Select a qualified or replied lead above and let AI find 10 similar companies.</p>
        </div>
      )}
    </div>
  );
}
